import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  EmailPreview,
  getEmailPreview,
  getReviewItem,
  getReviewList,
  getReviewOverview,
  ItemDetail,
  LanguageProgress,
  postReviewAction,
  ReviewAction,
  ReviewGroup,
  ReviewItem,
  ReviewList,
  ReviewStatus,
} from '../../services/translationsReview'
import WordPanelPreview from './WordPanelPreview'
import './translations-dashboard.css'

// Staff and freelance reviewer page (/dashboard/translations). English only, like the other
// staff pages. API: dev-backend/src/core/translations/review/ (ReviewerGuard on every route).

export const LANGUAGE_NAMES: Record<string, string> = {
  fr: 'French',
  es: 'Spanish',
  ja: 'Japanese',
  de: 'German',
  it: 'Italian',
  pt: 'Portuguese',
  ko: 'Korean',
}

const GROUP_NAMES: Record<ReviewGroup, string> = {
  interface: 'Interface',
  email: 'Emails and push',
  glossary: 'Glossary',
  word: 'Words',
  story: 'Stories',
  other: 'Other',
}
const GROUPS = Object.keys(GROUP_NAMES) as ReviewGroup[]

export const STATUS_NAMES: Record<ReviewStatus, string> = {
  to_check: 'To check',
  approved: 'Approved',
  edited: 'Edited',
  flagged: 'Flagged',
}

const TAG_NAMES: Record<string, string> = {
  missing: 'no translation yet',
  stale: 'Chinese changed',
  long: 'may not fit',
  attention: 'needs a human',
  checks: 'failed a check',
  reported: 'reported by learners',
  new: 'not reviewed yet',
  changed: 'changed since review',
}

/** Which items the list shows. */
export type Scope = 'core' | 'new' | 'reported' | 'all'
const SCOPES: { id: Scope; name: string }[] = [
  { id: 'core', name: 'Core set' },
  { id: 'new', name: 'New since review' },
  { id: 'reported', name: 'Reported' },
  { id: 'all', name: 'Everything' },
]

export interface Filters {
  scope: Scope
  group: ReviewGroup | 'all'
  screen: string
  status: ReviewStatus | 'all'
  query: string
}
export const DEFAULT_FILTERS: Filters = { scope: 'core', group: 'all', screen: 'all', status: 'all', query: '' }

export const filterItems = (items: ReviewItem[], f: Filters): ReviewItem[] => {
  const q = f.query.trim().toLowerCase()
  return items.filter((item) => {
    if (f.scope === 'core' && !item.core) return false
    if (f.scope === 'new' && !(item.tags.includes('new') || item.tags.includes('changed'))) return false
    if (f.scope === 'reported' && !item.tags.includes('reported')) return false
    if (f.group !== 'all' && item.group !== f.group) return false
    if (f.screen !== 'all' && item.screen !== f.screen) return false
    if (f.status !== 'all' && item.status !== f.status) return false
    if (q && ![item.label, item.english, item.text || '', item.chinese || ''].some((s) => s.toLowerCase().includes(q))) return false
    return true
  })
}

const DONE: ReviewStatus[] = ['approved', 'edited']

/** Progress from the items, as the server computes it (core items only). */
export const progressOf = (lang: string, items: ReviewItem[], live = false): LanguageProgress => {
  const groups = Object.fromEntries(GROUPS.map((g) => [g, { total: 0, done: 0 }])) as LanguageProgress['groups']
  const core = { total: 0, approved: 0, edited: 0, flagged: 0, toCheck: 0, missing: 0 }
  for (const item of items) {
    if (!item.core) continue
    core.total++
    groups[item.group].total++
    if (DONE.includes(item.status)) groups[item.group].done++
    if (item.status === 'approved') core.approved++
    else if (item.status === 'edited') core.edited++
    else if (item.status === 'flagged') core.flagged++
    else core.toCheck++
    if (item.tags.includes('missing')) core.missing++
  }
  return { lang, live, core, groups, ready: core.total > 0 && core.approved + core.edited === core.total }
}

const pct = (done: number, total: number) => (total ? Math.floor((done / total) * 100) : 0)

const ProgressBar: React.FC<{ done: number; total: number; label: string }> = ({ done, total, label }) => (
  <div
    className='td-bar'
    role='progressbar'
    aria-label={label}
    aria-valuemin={0}
    aria-valuemax={total}
    aria-valuenow={done}
  >
    <div className='td-bar-fill' style={{ width: `${pct(done, total)}%` }} />
  </div>
)

/** Where "Open in place" goes: the page, in the language, with the string outlined. */
export const inPlaceUrl = (path: string, lang: string, highlight: string) => {
  const [route, hash] = path.split('#')
  const params = new URLSearchParams({ lang, i18nHighlight: highlight })
  return `${route}?${params.toString()}${hash ? `#${hash}` : ''}`
}

const isTyping = (target: EventTarget | null) => {
  const el = target as HTMLElement | null
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)
}

const forbidden = (e: any) => e?.response?.status === 403
const serverMessage = (e: any, fallback: string) => {
  const message = e?.response?.data?.message
  return typeof message === 'string' && message !== 'Error' ? message : fallback
}

const LIST_PAGE = 300

const TranslationsDashboard: React.FC = () => {
  const [languages, setLanguages] = useState<LanguageProgress[] | null>(null)
  const [pageError, setPageError] = useState('')
  const [lang, setLang] = useState<string | null>(null)
  const [list, setList] = useState<ReviewList | null>(null)
  const [listLoading, setListLoading] = useState(false)
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS)
  const [shown, setShown] = useState(LIST_PAGE)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<ItemDetail | null>(null)
  const [detailError, setDetailError] = useState('')
  const [text, setText] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [actionError, setActionError] = useState('')
  const [email, setEmail] = useState<EmailPreview | null>(null)
  const textRef = useRef<HTMLTextAreaElement | null>(null)
  const noteRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    getReviewOverview()
      .then((langs) => {
        setLanguages(langs)
        let remembered: string | null = null
        try {
          remembered = window.localStorage.getItem('td.lang')
        } catch {
          /* storage blocked: start on the first language */
        }
        const first = langs.find((l) => l.lang === remembered) || langs[0]
        if (first) setLang(first.lang)
      })
      .catch((e) =>
        setPageError(forbidden(e) ? 'This page is for translation reviewers.' : 'Could not load the review. Try again in a moment.'),
      )
  }, [])

  useEffect(() => {
    if (!lang) return
    try {
      window.localStorage.setItem('td.lang', lang)
    } catch {
      /* not remembered: fine */
    }
    setListLoading(true)
    setList(null)
    setSelectedId(null)
    setDetail(null)
    setFilters(DEFAULT_FILTERS)
    getReviewList(lang)
      .then(setList)
      .catch((e) => setPageError(forbidden(e) ? 'This page is for translation reviewers.' : 'Could not load this language.'))
      .finally(() => setListLoading(false))
  }, [lang])

  const visible = useMemo(() => (list ? filterItems(list.items, filters) : []), [list, filters])
  const screens = useMemo(() => {
    const pool = list ? filterItems(list.items, { ...filters, screen: 'all' }) : []
    return Array.from(new Set(pool.map((i) => i.screen))).sort()
  }, [list, filters])
  const progress = useMemo(() => (list && lang ? progressOf(lang, list.items, list.progress.live) : null), [list, lang])

  // Load the selected item's detail, and its email preview.
  useEffect(() => {
    if (!lang || !selectedId) return
    let current = true
    setDetail(null)
    setDetailError('')
    setActionError('')
    setEmail(null)
    getReviewItem(lang, selectedId)
      .then((d) => {
        if (!current) return
        setDetail(d)
        setText(d.item.text || '')
        setNote(d.item.note || '')
        if (d.context.emailPreview) {
          getEmailPreview(lang, d.context.emailPreview)
            .then((p) => current && setEmail(p))
            .catch(() => undefined)
        }
      })
      .catch(() => current && setDetailError('Could not load this item.'))
    return () => {
      current = false
    }
  }, [lang, selectedId])

  const move = useCallback(
    (step: number) => {
      if (!visible.length) return
      const at = visible.findIndex((i) => i.id === selectedId)
      const next = at < 0 ? 0 : Math.min(visible.length - 1, Math.max(0, at + step))
      setSelectedId(visible[next].id)
    },
    [visible, selectedId],
  )

  const act = useCallback(
    async (action: ReviewAction) => {
      if (!lang || !detail || saving) return
      setActionError('')
      if (action === 'edit' && !text.trim()) {
        setActionError('Write the corrected translation first.')
        textRef.current?.focus()
        return
      }
      if (action === 'flag' && !note.trim()) {
        setActionError('Say what is wrong to flag it.')
        noteRef.current?.focus()
        return
      }
      setSaving(true)
      try {
        const item = await postReviewActionFor(lang, detail.item.id, action, text, note)
        setList((prev) => (prev ? { ...prev, items: prev.items.map((i) => (i.id === item.id ? item : i)) } : prev))
        setDetail((prev) => (prev ? { ...prev, item } : prev))
        if (action === 'approve' || action === 'edit') move(1)
      } catch (e: any) {
        setActionError(serverMessage(e, 'Could not save. Try again.'))
      } finally {
        setSaving(false)
      }
    },
    [lang, detail, saving, text, note, move],
  )

  // Keyboard: j/k (or arrows) to move, a approve, e edit the text, f write a flag note.
  // In the text box, Ctrl or Cmd + Enter saves the edit; in the note, Enter flags.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && e.target === textRef.current) {
          e.preventDefault()
          act('edit')
        } else if (e.key === 'Enter' && e.target === noteRef.current) {
          e.preventDefault()
          act('flag')
        } else if (e.key === 'Escape') {
          ;(e.target as HTMLElement).blur()
        }
        return
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return
      if (e.key === 'j' || e.key === 'ArrowDown') {
        e.preventDefault()
        move(1)
      } else if (e.key === 'k' || e.key === 'ArrowUp') {
        e.preventDefault()
        move(-1)
      } else if (e.key === 'a') {
        act('approve')
      } else if (e.key === 'e') {
        e.preventDefault()
        textRef.current?.focus()
      } else if (e.key === 'f') {
        e.preventDefault()
        noteRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [act, move])

  if (pageError) {
    return (
      <div className='td-page'>
        <div className='td-wrap'>
          <h1>Translation review</h1>
          <p className='td-error'>{pageError}</p>
        </div>
      </div>
    )
  }

  const setFilter = (patch: Partial<Filters>) => {
    setFilters((f) => ({ ...f, ...patch }))
    setShown(LIST_PAGE)
  }

  return (
    <div className='td-page'>
      <div className='td-wrap'>
        <header className='td-header'>
          <h1>Translation review</h1>
          <p className='td-sub'>
            Check each item against the English. A language launches when every core item is approved or edited.
          </p>
        </header>

        {!languages ? (
          <p className='td-msg'>Loading</p>
        ) : (
          <nav className='td-langs' aria-label='Languages'>
            {languages.map((l) => {
              const done = l.core.approved + l.core.edited
              return (
                <button
                  key={l.lang}
                  type='button'
                  className={`td-lang${l.lang === lang ? ' td-lang-on' : ''}`}
                  onClick={() => setLang(l.lang)}
                  aria-pressed={l.lang === lang}
                >
                  <span className='td-lang-name'>{LANGUAGE_NAMES[l.lang] || l.lang}</span>
                  <span className='td-lang-meta'>
                    {l.live ? 'Live' : l.ready ? 'Ready to launch' : `${pct(done, l.core.total)}% of the core set`}
                  </span>
                  <ProgressBar done={done} total={l.core.total} label={`${LANGUAGE_NAMES[l.lang] || l.lang} progress`} />
                </button>
              )
            })}
          </nav>
        )}

        {listLoading && <p className='td-msg'>Loading the items</p>}

        {list && progress && (
          <>
            <section className='td-summary'>
              <div>
                <div className='td-big'>
                  {progress.core.approved + progress.core.edited} of {progress.core.total} core items signed off
                </div>
                <ProgressBar done={progress.core.approved + progress.core.edited} total={progress.core.total} label='Core set progress' />
                <p className='td-sub'>
                  {progress.ready
                    ? 'The core set is fully approved: this language can launch.'
                    : `${progress.core.toCheck} to check, ${progress.core.flagged} flagged, ${progress.core.missing} not translated yet.`}
                </p>
              </div>
              <div className='td-groups'>
                {GROUPS.filter((g) => progress.groups[g].total > 0).map((g) => (
                  <div key={g} className='td-group'>
                    <span>{GROUP_NAMES[g]}</span>
                    <span className='td-muted'>
                      {progress.groups[g].done} / {progress.groups[g].total}
                    </span>
                  </div>
                ))}
              </div>
              <p className='td-sub td-facts'>
                {list.afterLaunch.newStrings} strings not reviewed yet, {list.afterLaunch.changedStrings} changed since review,{' '}
                {list.afterLaunch.openReports} items reported by learners.
                {list.audit
                  ? ` Last audit ${new Date(list.audit.at).toLocaleDateString(undefined, { dateStyle: 'medium' })}${
                      list.audit.overdueStories ? `: ${list.audit.overdueStories} stories overdue` : ''
                    }.`
                  : ''}
              </p>
            </section>

            <section className='td-filters' aria-label='Filters'>
              <div className='td-scopes'>
                {SCOPES.map((s) => (
                  <button
                    key={s.id}
                    type='button'
                    className={`td-scope${filters.scope === s.id ? ' td-scope-on' : ''}`}
                    onClick={() => setFilter({ scope: s.id })}
                    aria-pressed={filters.scope === s.id}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
              <label>
                Group
                <select value={filters.group} onChange={(e) => setFilter({ group: e.target.value as Filters['group'], screen: 'all' })}>
                  <option value='all'>All groups</option>
                  {GROUPS.map((g) => (
                    <option key={g} value={g}>
                      {GROUP_NAMES[g]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Screen
                <select value={filters.screen} onChange={(e) => setFilter({ screen: e.target.value })}>
                  <option value='all'>All screens</option>
                  {screens.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Status
                <select value={filters.status} onChange={(e) => setFilter({ status: e.target.value as Filters['status'] })}>
                  <option value='all'>All statuses</option>
                  {(Object.keys(STATUS_NAMES) as ReviewStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {STATUS_NAMES[s]}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Search
                <input value={filters.query} onChange={(e) => setFilter({ query: e.target.value })} placeholder='Key, English or translation' />
              </label>
            </section>

            <div className='td-main'>
              <ul className='td-list' aria-label='Items'>
                {visible.length === 0 && <li className='td-msg'>Nothing here with these filters.</li>}
                {visible.slice(0, shown).map((item) => (
                  <li key={item.id}>
                    <button
                      type='button'
                      className={`td-row${item.id === selectedId ? ' td-row-on' : ''}`}
                      onClick={() => setSelectedId(item.id)}
                      aria-current={item.id === selectedId}
                    >
                      <span className='td-row-head'>
                        <span className='td-row-label' lang={item.group === 'word' ? 'zh-Hans' : undefined}>
                          {item.label}
                        </span>
                        <span className={`td-status td-status-${item.status}`}>{STATUS_NAMES[item.status]}</span>
                      </span>
                      <span className='td-row-text' lang={lang || undefined}>
                        {item.text || <em className='td-muted'>no translation yet</em>}
                      </span>
                      <span className='td-row-meta'>
                        {item.screen}
                        {item.tags.filter((t) => t !== 'missing').map((t) => (
                          <span key={t} className={`td-tag${t === 'long' || t === 'attention' ? ' td-tag-warn' : ''}`}>
                            {TAG_NAMES[t]}
                          </span>
                        ))}
                      </span>
                    </button>
                  </li>
                ))}
                {visible.length > shown && (
                  <li>
                    <button type='button' className='td-more' onClick={() => setShown((n) => n + LIST_PAGE)}>
                      Show more ({visible.length - shown} left)
                    </button>
                  </li>
                )}
              </ul>

              <section className='td-detail' aria-label='Item'>
                {!selectedId && <p className='td-msg'>Pick an item, or press j to start.</p>}
                {selectedId && !detail && !detailError && <p className='td-msg'>Loading</p>}
                {detailError && <p className='td-error'>{detailError}</p>}
                {detail && lang && (
                  <DetailPane
                    detail={detail}
                    lang={lang}
                    text={text}
                    setText={setText}
                    note={note}
                    setNote={setNote}
                    saving={saving}
                    actionError={actionError}
                    onAction={act}
                    textRef={textRef}
                    noteRef={noteRef}
                    email={email}
                  />
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

const postReviewActionFor = (lang: string, id: string, action: ReviewAction, text: string, note: string) =>
  postReviewAction(lang, {
    id,
    action,
    ...(action === 'edit' ? { text: text.trim() } : {}),
    ...(action === 'flag' ? { note: note.trim() } : {}),
  })

interface DetailProps {
  detail: ItemDetail
  lang: string
  text: string
  setText: (v: string) => void
  note: string
  setNote: (v: string) => void
  saving: boolean
  actionError: string
  onAction: (a: ReviewAction) => void
  textRef: React.MutableRefObject<HTMLTextAreaElement | null>
  noteRef: React.MutableRefObject<HTMLInputElement | null>
  email: EmailPreview | null
}

const DetailPane: React.FC<DetailProps> = ({ detail, lang, text, setText, note, setNote, saving, actionError, onAction, textRef, noteRef, email }) => {
  const { item, context } = detail
  const locked = item.status === 'approved' || item.status === 'edited'
  return (
    <div className='td-detail-inner'>
      <div className='td-detail-head'>
        <h2 lang={item.group === 'word' ? 'zh-Hans' : undefined}>{item.label}</h2>
        <span className={`td-status td-status-${item.status}`}>
          {STATUS_NAMES[item.status]}
          {locked ? ', locked' : ''}
        </span>
      </div>
      <p className='td-muted'>
        {context.screen}
        {item.core ? ', core set' : ''}
        {item.tags.length ? `, ${item.tags.map((t) => TAG_NAMES[t]).join(', ')}` : ''}
      </p>
      {item.status === 'flagged' && item.note && <p className='td-note'>Flag note: {item.note}</p>}
      {item.checks?.length ? <p className='td-note'>Failed checks: {item.checks.join(', ')}</p> : null}

      <dl className='td-fields'>
        {item.chinese && (
          <>
            <dt>Chinese</dt>
            <dd lang='zh-Hans'>{item.chinese}</dd>
          </>
        )}
        <dt>English</dt>
        <dd lang='en'>{item.english || <span className='td-muted'>none</span>}</dd>
        <dt>AI draft</dt>
        <dd lang={lang}>{detail.draft || <span className='td-muted'>none yet</span>}</dd>
      </dl>
      {detail.reports.length > 0 && (
        <div className='td-reports'>
          <h3>What learners said</h3>
          <ul>
            {detail.reports.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      <label className='td-edit'>
        Translation
        <textarea ref={textRef} value={text} onChange={(e) => setText(e.target.value)} lang={lang} rows={3} />
      </label>
      <div className='td-actions'>
        <button type='button' className='td-btn td-btn-main' disabled={saving} onClick={() => onAction('approve')}>
          Approve <kbd>A</kbd>
        </button>
        <button type='button' className='td-btn' disabled={saving} onClick={() => onAction('edit')}>
          Save edit <kbd>Ctrl Enter</kbd>
        </button>
        <input
          ref={noteRef}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder='What is wrong (needed to flag)'
          aria-label='Flag note'
        />
        <button type='button' className='td-btn' disabled={saving} onClick={() => onAction('flag')}>
          Flag <kbd>F</kbd>
        </button>
        {item.status !== 'to_check' && (
          <button type='button' className='td-btn td-btn-quiet' disabled={saving} onClick={() => onAction('reset')}>
            Back to check
          </button>
        )}
      </div>
      {actionError && <p className='td-error'>{actionError}</p>}

      <div className='td-context'>
        <h3>In place</h3>
        {context.word && (
          <>
            <WordPanelPreview word={context.word} lang={lang} text={text} sentence={context.sentences?.[0] || null} />
            {(context.sentences || []).length > 0 && (
              <ul className='td-sentences'>
                {(context.sentences || []).map((s, i) => (
                  <li key={i}>
                    <p lang='zh-Hans'>{s.chinese}</p>
                    {s.english && <p className='td-muted' lang='en'>{s.english}</p>}
                    <p lang={lang}>{s.translation || <span className='td-muted'>no translation yet</span>}</p>
                    <p className='td-muted'>{s.title}</p>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {context.highlight && (
          <>
            <p>
              Key <code>{context.highlight}</code>
              {context.path && (
                <>
                  {' '}
                  <a href={inPlaceUrl(context.path, lang, context.highlight)} target='_blank' rel='noreferrer'>
                    Open the page in {LANGUAGE_NAMES[lang] || lang}
                  </a>
                </>
              )}
            </p>
          </>
        )}
        {context.neighbours && context.neighbours.length > 0 && (
          <table className='td-neighbours'>
            <thead>
              <tr>
                <th>Next to it</th>
                <th>English</th>
                <th>{LANGUAGE_NAMES[lang] || lang}</th>
              </tr>
            </thead>
            <tbody>
              {context.neighbours.map((n) => (
                <tr key={n.key}>
                  <td className='td-muted'>{n.key}</td>
                  <td lang='en'>{n.english}</td>
                  <td lang={lang}>{n.text || <span className='td-muted'>none</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {context.emailPreview && (
          <div className='td-email'>
            {email ? (
              <>
                <p>
                  Subject: <strong>{email.subject}</strong>
                </p>
                <iframe title='Email preview' sandbox='' srcDoc={email.html} />
                <p className='td-muted'>The preview shows the strings as shipped: an edit appears here once it is exported.</p>
              </>
            ) : (
              <p className='td-muted'>Loading the email</p>
            )}
          </div>
        )}
        {context.story && (
          <div className='td-story'>
            <p>
              <span lang='zh-Hans'>{context.story.title}</span> ({context.story.englishTitle}
              {context.story.level ? `, ${context.story.level.replace('_', ' ').toLowerCase()}` : ''})
            </p>
            {context.story.before && <p className='td-muted' lang='zh-Hans'>{context.story.before}</p>}
            {item.chinese && <p lang='zh-Hans'><strong>{item.chinese}</strong></p>}
            {context.story.after && <p className='td-muted' lang='zh-Hans'>{context.story.after}</p>}
          </div>
        )}
      </div>
    </div>
  )
}

export default TranslationsDashboard
